import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { FIN_HELPER_API_BASE } from '../config';
import './PricingHistory.css';

const BRAND_COLOR = '#3e3dc5';

/** Parse dd/mm/yyyy to Date for comparison */
function parseDate(s) {
  if (!s) return null;
  const parts = String(s).trim().split('/');
  if (parts.length !== 3) return null;
  const [d, m, y] = parts;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? null : date;
}

function PriceTooltip({ active, payload, label }) {
  if (!active || !payload?.length || !payload[0].payload) return null;
  const { date_bought, item_price, store_name } = payload[0].payload;
  return (
    <div className="pricing-history__tooltip">
      <div className="pricing-history__tooltip-row">
        <span className="pricing-history__tooltip-label">Date:</span>
        {date_bought}
      </div>
      <div className="pricing-history__tooltip-row">
        <span className="pricing-history__tooltip-label">Store:</span>
        {store_name}
      </div>
      <div className="pricing-history__tooltip-row">
        <span className="pricing-history__tooltip-label">Price:</span>
        {item_price}
      </div>
    </div>
  );
}

export default function PricingHistory() {
  const { token, logout } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('milka');
  const [query, setQuery] = useState('milka');
  const [selectedStore, setSelectedStore] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    if (!query) return;
    setLoading(true);
    setError(null);
    axios
      .get(`${FIN_HELPER_API_BASE}/search/items/?item_name=${encodeURIComponent(query)}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        const items = res.data.items
          .map((item) => ({
            ...item,
            date_bought: item.date_bought.split(' ')[0],
          }))
          .sort((a, b) => {
            const [da, ma, ya] = a.date_bought.split('/');
            const [db, mb, yb] = b.date_bought.split('/');
            return new Date(`${ya}-${ma}-${da}`) - new Date(`${yb}-${mb}-${db}`);
          });
        setData(items);
        setSelectedStore(null);
      })
      .catch(() => {
        setError('Failed to fetch data');
      })
      .finally(() => setLoading(false));
  }, [query, token]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setQuery(search);
  };

  const chartDataRaw = selectedStore
    ? data.filter((item) => item.store_name === selectedStore)
    : data;

  const chartData = (() => {
    let list = chartDataRaw;
    const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    const to = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;
    if (from) list = list.filter((item) => parseDate(item.date_bought) >= from);
    if (to) list = list.filter((item) => parseDate(item.date_bought) <= to);
    return list;
  })();

  return (
    <div className="pricing-history">
      <header className="pricing-history__header" style={{ background: BRAND_COLOR }}>
        <h1 className="pricing-history__title">MONETA Pricing History</h1>
        <button
          type="button"
          className="pricing-history__logout"
          onClick={logout}
        >
          Log out
        </button>
      </header>
      <main className="pricing-history__main">
        <section className="pricing-history__sidebar">
          <form onSubmit={handleSubmit} className="pricing-history__form">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search item name..."
              className="pricing-history__input"
              style={{
                borderColor: BRAND_COLOR,
                boxShadow: `0 0 0 2px ${BRAND_COLOR}22`,
              }}
            />
            <button type="submit" className="pricing-history__btn" style={{ background: BRAND_COLOR }}>
              Search
            </button>
          </form>
          <div className="pricing-history__table-wrap">
            {loading && <p className="pricing-history__msg">Loading...</p>}
            {error && <p className="pricing-history__msg pricing-history__msg--error">{error}</p>}
            {!loading && !error && data.length === 0 && (
              <p className="pricing-history__msg">No data found.</p>
            )}
            {!loading && !error && data.length > 0 && (
              <table className="pricing-history__table">
                <thead>
                  <tr>
                    <th>Item Name</th>
                    <th>Price</th>
                    <th>Date</th>
                    <th>Store</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, idx) => (
                    <tr key={idx} className="pricing-history__row">
                      <td>{item.item_name}</td>
                      <td>{item.item_price}</td>
                      <td>{item.date_bought}</td>
                      <td
                        className={`pricing-history__store-cell ${selectedStore === item.store_name ? 'pricing-history__store-cell--active' : ''}`}
                        style={
                          selectedStore === item.store_name
                            ? { background: BRAND_COLOR, color: 'white' }
                            : { color: BRAND_COLOR, background: '#f5f5ff' }
                        }
                        onClick={() => setSelectedStore(item.store_name)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && setSelectedStore(item.store_name)}
                      >
                        {item.store_name}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {!loading && !error && data.length > 0 && (
            <div className="pricing-history__sidebar-actions">
              <button
                type="button"
                className="pricing-history__clear-btn"
                disabled={!selectedStore}
                onClick={() => setSelectedStore(null)}
              >
                Show All Stores
              </button>
            </div>
          )}
        </section>
        <section className="pricing-history__chart-section">
          {!loading && !error && chartDataRaw.length > 0 && (
            <>
              <div className="pricing-history__chart-filters" aria-label="Graph data filter">
                <label>
                  <span>Periudha – fillim</span>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    aria-label="Start date"
                  />
                </label>
                <label>
                  <span>Periudha – mbarim</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    aria-label="End date"
                  />
                </label>
                <button
                  type="button"
                  className="pricing-history__reset-date-btn"
                  disabled={!dateFrom && !dateTo}
                  onClick={() => {
                    setDateFrom('');
                    setDateTo('');
                  }}
                >
                  Reset date
                </button>
              </div>
              <div className="pricing-history__chart-container">
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={250}>
                    <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date_bought" angle={-45} textAnchor="end" height={70} />
                      <YAxis />
                      <Tooltip content={<PriceTooltip />} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="item_price"
                        stroke={BRAND_COLOR}
                        name="Price"
                        strokeWidth={3}
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="pricing-history__msg">No data in the selected date range.</p>
                )}
              </div>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
